import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useMovementPageData } from '#src/app/pages/movement/pageContext';
import { useCurrentUser } from '#src/app/shared/hooks/session';
import type { ShareAction, ShareActionsOptions } from '#src/app/shared/hooks/share';
import { useShareActions } from '#src/app/shared/hooks/share';

export const MOVEMENT_EVENT_VIEW_CONTEXT = 'movement_show';

export function useMovementShareActions(): readonly ShareAction[] {
	const {
		data: {
			movement: { id: movementId, objective, shareLinks },
		},
	} = useMovementPageData();
	const currentUser = useCurrentUser();
	const { translate } = useI18n();
	const userName = currentUser?.shortDisplayName ?? '';

	const getContent: ShareActionsOptions['getContent'] = useCallback(
		(channel, type, { url }) => {
			if (type === 'body') {
				return channel === 'email'
					? translate('corgi.movement.show.share.email_body', { objective, url, from: userName })
					: '';
			}
			if (type === 'title') {
				return channel === 'twitter' ? translate('corgi.movement.show.share.twitter', { objective }) : objective;
			}
			return '';
		},
		[translate, objective, userName],
	);

	const getTrackingData: ShareActionsOptions['getTrackingData'] = useCallback(
		(channel) => ({
			location: channel,
			movement_id: movementId,
			context: MOVEMENT_EVENT_VIEW_CONTEXT,
			view_context: MOVEMENT_EVENT_VIEW_CONTEXT,
		}),
		[movementId],
	);

	return useShareActions(shareLinks.links, {
		trackingEventName: 'movement_share',
		getContent,
		getTrackingData,
	});
}
